import { InboxOutlined } from "@ant-design/icons";
import { Button, Modal, Radio, Space, Typography, Upload, message } from "antd";
import Papa from "papaparse";
import { useEffect, useState } from "react";
import { useMutation } from "react-query";
import { appInject } from "@core/di/utils";
import { DI_TOKENS } from "@shared/constants/di";
import { IPlayerCardService } from "@shared/interfaces/player-card-service.interface";
import {
  ApplyJerseyNumbersFromCsvMode,
  CsvJerseyPlayer,
} from "../Players.types";

type CsvRow = {
  playerId?: string;
  jerseyNumber?: string;
  displayName?: string;
};

interface ImportJerseyNumbersCsvModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const parseJerseyNumbersCsv = (
  file: File,
): Promise<{ players: CsvJerseyPlayer[]; fileName: string }> =>
  new Promise((resolve, reject) => {
    Papa.parse<CsvRow>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        const columns = Object.keys(result.data[0] || {});
        if (
          !columns.includes("playerId") ||
          !columns.includes("jerseyNumber")
        ) {
          reject(
            new Error(
              'CSV must include "playerId" and "jerseyNumber" columns.',
            ),
          );
          return;
        }

        const playersById = new Map<number, CsvJerseyPlayer>();

        for (const row of result.data) {
          const playerID = Number(row.playerId);
          const jerseyNumber = Number(row.jerseyNumber);

          if (!Number.isFinite(playerID) || playerID <= 0) {
            continue;
          }
          if (!Number.isFinite(jerseyNumber) || jerseyNumber === 0) {
            continue;
          }
          if (jerseyNumber < 1 || jerseyNumber > 99) {
            continue;
          }

          playersById.set(playerID, {
            playerID,
            jerseyNumber,
            displayName: row.displayName?.trim() || undefined,
          });
        }

        const players = Array.from(playersById.values());
        if (players.length === 0) {
          reject(
            new Error(
              "No eligible players found (jerseyNumber must be a non-zero number).",
            ),
          );
          return;
        }

        resolve({ players, fileName: file.name });
      },
      error: () => {
        reject(new Error("Failed to parse CSV file."));
      },
    });
  });

const ImportJerseyNumbersCsvModal = ({
  open,
  onClose,
  onSuccess,
}: ImportJerseyNumbersCsvModalProps) => {
  const [mode, setMode] =
    useState<ApplyJerseyNumbersFromCsvMode>("fillMissing");
  const [players, setPlayers] = useState<CsvJerseyPlayer[]>([]);
  const [fileName, setFileName] = useState("");
  const [parsing, setParsing] = useState(false);

  const playerCardsService = appInject<IPlayerCardService>(
    DI_TOKENS.playerCardService,
  );

  useEffect(() => {
    if (!open) {
      setMode("fillMissing");
      setPlayers([]);
      setFileName("");
      setParsing(false);
    }
  }, [open]);

  const applyMutation = useMutation(
    () =>
      playerCardsService.applyJerseyNumbersFromCsv({
        mode,
        players,
      }),
    {
      onSuccess: (response) => {
        const result = response?.data;
        void message.success(
          result
            ? `Updated ${result.totalUpdated} cards (${result.skippedAlreadySet} already set, ${result.skippedUnmappedPlayerId} unmatched).`
            : "Jersey numbers applied.",
        );
        onSuccess();
      },
      onError: () => {
        void message.error("Failed to apply jersey numbers from CSV");
      },
    },
  );

  const handleFile = (file: File) => {
    setParsing(true);
    void parseJerseyNumbersCsv(file)
      .then(({ players: parsedPlayers, fileName: parsedFileName }) => {
        setPlayers(parsedPlayers);
        setFileName(parsedFileName);
      })
      .catch((error: Error) => {
        void message.error(error.message || "Failed to parse CSV");
      })
      .finally(() => {
        setParsing(false);
      });
  };

  return (
    <Modal
      title="Import jersey numbers from CSV"
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
    >
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        {players.length === 0 ? (
          <Upload.Dragger
            accept=".csv,text/csv"
            multiple={false}
            showUploadList={false}
            disabled={parsing}
            beforeUpload={(file) => {
              handleFile(file);
              return false;
            }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              {parsing
                ? "Parsing CSV…"
                : "Drop player CSV or click to choose a file"}
            </p>
            <p className="ant-upload-hint">
              Uses playerId + jerseyNumber. Rows with jerseyNumber 0 are
              skipped.
            </p>
          </Upload.Dragger>
        ) : (
          <>
            <Typography.Text>
              File: <strong>{fileName}</strong>
            </Typography.Text>
            <Typography.Text>
              Eligible players (jersey # ≠ 0):{" "}
              <strong>{players.length}</strong>
            </Typography.Text>

            <div>
              <Typography.Paragraph style={{ marginBottom: 8 }}>
                How should existing jersey numbers be handled?
              </Typography.Paragraph>
              <Radio.Group
                value={mode}
                onChange={(event) => setMode(event.target.value)}
              >
                <Space direction="vertical">
                  <Radio value="fillMissing">
                    Only fill missing (skip cards that already have a jersey #)
                  </Radio>
                  <Radio value="overwrite">
                    Overwrite existing jersey numbers too
                  </Radio>
                </Space>
              </Radio.Group>
            </div>

            <Space style={{ justifyContent: "flex-end", width: "100%" }}>
              <Button
                onClick={() => {
                  setPlayers([]);
                  setFileName("");
                }}
              >
                Choose another file
              </Button>
              <Button onClick={onClose}>Cancel</Button>
              <Button
                type="primary"
                loading={applyMutation.isLoading}
                onClick={() => applyMutation.mutate()}
              >
                Apply
              </Button>
            </Space>
          </>
        )}
      </Space>
    </Modal>
  );
};

export default ImportJerseyNumbersCsvModal;
